import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { IconUserPlus } from '@tabler/icons-react';
import { addDoc, getDoc } from 'firebase/firestore';
import { isEmpty } from 'lodash';
import { useState } from 'react';
import Functions from '../../../../callable/functions';
import UsuarioDb from '../../../../db/usuarioDb';
import felixAnalytics from '../../../../felixAnalytics';
import Business from '../../../../models/business';
import { Perfil } from '../../../../models/enum';
import Usuario from '../../../../models/usuario';
import { useSnackBar } from '../../../../stores/snackBarStores';
import FelixAvatar from '../../../util/ui/felixAvatar';

type Props = {
  business: Business;
  open: boolean;
  onClose: (success: boolean) => void;
  limiteAtingido?: boolean;
};

export default function CriarClienteDialog({
  business,
  open,
  onClose,
  limiteAtingido,
}: Props) {
  const [nome, setNome] = useState<string>('');
  const [portalLoading, setPortalLoading] = useState(false);
  const [email, setEmail] = useState<string>('');
  const [criandoCliente, setCriandoCliente] = useState(false);
  const snackBar = useSnackBar();

  const criarCliente = async () => {
    setCriandoCliente(true);
    await addDoc(new UsuarioDb().collection({ usuarioId: '' }), {
      nome: nome,
      contaPai: business.id,
    } as Usuario)
      .then(async (clienteRef) => {
        const clienteDoc = await getDoc(clienteRef);
        const cliente = clienteDoc.data();
        if (!!cliente && !!email) {
          felixAnalytics.track('convite-cliente-enviar');
          return new Functions().enviarConviteCliente({
            id: cliente.id,
            nome: cliente.nome,
            email,
          });
        }
      })
      .then(() => {
        snackBar({
          type: 'ALERT',
          payload: {
            mensagem: `O cliente ${nome} foi criado.`,
            severidade: 'success',
          },
        });
      })
      .catch((error) => {
        snackBar({
          type: 'ALERT',
          payload: {
            mensagem:
              error.details.code === 'auth/email-already-exists'
                ? `Esse e-mail já está cadastrado na Felix.`
                : `Houve um erro ao enviar o convite para o cliente. Por favor, tente novamente ou entre em contato com nosso suporte.`,
            severidade: 'error',
          },
        });
        console.error(error);
      })
      .finally(() => setCriandoCliente(false))
      .finally(() => setNome(''))
      .finally(() => setEmail(''))
      .finally(() => onClose(true));
  };

  return (
    <Dialog open={open} maxWidth="xs" fullWidth>
      <DialogTitle color="text.primary">
        <Box display="flex" flexDirection="row" alignItems="center">
          <Box mr={1}>
            <IconUserPlus size={16} />
          </Box>
          <Typography variant="h4">
            {limiteAtingido ? 'Upgrade de plano' : 'Adicionar cliente'}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {limiteAtingido ? (
          <Box display="flex" flexDirection="column" alignItems="center">
            <Typography textAlign="center" color="text.secondary">
              Você atingiu o limite de clientes do seu plano atual. Faça um
              upgrade para continuar adicionando novos clientes.
            </Typography>
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" alignItems="center">
            <FelixAvatar nome={nome} size={64} perfil={Perfil.INVESTIDOR} />
            <TextField
              sx={{ my: 2 }}
              size="small"
              margin="none"
              fullWidth
              id="nome"
              label="Nome"
              name="nome"
              autoComplete="nome"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
            />
            <TextField
              sx={{ my: 2 }}
              size="small"
              margin="none"
              fullWidth
              id="email"
              label="Email"
              helperText="Este campo é opcional. Caso preenchido, enviaremos um convite para o cliente acessar a Felix na Bolsa de acordo com as permissões que você definir."
              name="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)} color="inherit">
          Cancelar
        </Button>
        {limiteAtingido ? (
          <LoadingButton
            variant="contained"
            loading={portalLoading}
            onClick={async () => {
              setPortalLoading(true);
              try {
                const { data } = await new Functions().obterLinkPainelDoCliente(
                  {
                    returnUrl: window.location.href,
                  },
                );
                window.open(data.url, '_blank');
              } finally {
                setPortalLoading(false);
              }

              onClose(false);
            }}
          >
            Fazer upgrade
          </LoadingButton>
        ) : (
          <LoadingButton
            variant="contained"
            size="small"
            color="primary"
            disabled={isEmpty(nome)}
            loading={criandoCliente}
            onClick={criarCliente}
          >
            Adicionar
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
}
